import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { IconDefinition, faComment, faExclamationTriangle } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck as faCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import { Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { SamwebWindowComponent } from 'src/app/components/utility/samweb-window/samweb-window.component';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { ChatJsApiService } from 'src/app/services/chat/js-api/chat-js-api-service';
import { ChatMessageSender } from 'src/app/services/chat/js-api/commands/chat-message-sender';
import { Command, CommandWithPayload } from 'src/app/services/chat/js-api/commands/command';
import { CommandACK } from 'src/app/services/chat/js-api/commands/command-ACK';
import { InitChatCommand } from 'src/app/services/chat/js-api/commands/init-chat-command';
import { UpdateLastMessageCommand } from 'src/app/services/chat/js-api/commands/update-last-message-command';
import { ProcessNewMessageCommand } from 'src/app/services/chat/js-api/commands/update-new-message-command';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { CallTagsSelectors } from 'src/app/store/samwin/call-tags/call-tags.selectors';
import { CallTagsState } from 'src/app/store/samwin/call-tags/call-tags.state';
import {
   CloseChat,
   IncrementNumberOfUnreadMessages,
   ResetNumberOfUnreadMessages,
   StartReQueueChat,
   UpdateLastMessage,
} from 'src/app/store/samwin/chats/chats.actions';
import { ChatsSelectors } from 'src/app/store/samwin/chats/chats.selectors';
import { ChatDetails } from 'src/app/store/samwin/chats/models/chat-details';
import { ChatGuestInfo } from 'src/app/store/samwin/chats/models/chat-guest-info';
import { ChatId } from 'src/app/store/samwin/chats/models/chat-id';
import { ChatMessage } from 'src/app/store/samwin/chats/models/chat-message';
import { ChatParticipantType } from 'src/app/store/samwin/chats/models/chat-participant-type';
import { SelectedSubscriberState } from 'src/app/store/samwin/directory/selected-subscriber/selected-subscriber.state';
import { HOTKEYS_DISABLED } from 'src/app/store/samwin/shortcut-behaviour/hotkeys-disabled';
import { LongRunningContextState } from 'src/app/store/server-communication/running-actions/long-running-context.state';
import { SamwebWindowState, WindowType, WindowTypes } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { CallTagsForSingleCallRemoteDto } from 'src/generated/signalR/call-tags-for-single-call-remote-dto';

@Component({
   selector: 'samweb-chat-dialog',
   templateUrl: './chat-dialog.component.html',
   styleUrls: ['./chat-dialog.component.scss'],
})
export class ChatDialogComponent implements OnInit, OnDestroy {
   @Input()
   public chatId: ChatId;

   @ViewChildren('chatIframe', { read: ElementRef })
   public chatIframe: QueryList<ElementRef>;

   @ViewChild('closeTooltipDirective')
   public closeTooltipDirective: TooltipDirective;

   @ViewChild(SamwebWindowComponent)
   public window: SamwebWindowComponent;

   private lastIsChatWindowOpen: boolean;
   private lastIsAppVisible: boolean;

   private destroy$ = new Subject();

   private chatIframeInitDone: boolean;
   private chatIframeInitTimer: NodeJS.Timer;

   private userToken: string;

   public windowTitle$: Observable<string>;
   public isLoading$: Observable<boolean>;
   public tags$: Observable<{ [key: string]: string }>;

   public chatDetails: ChatDetails;
   public chatGuestInfo$: Observable<ChatGuestInfo>;
   public rawChatUrl: string;
   public chatUrl: SafeUrl;
   public wasClosedByPeer: boolean;

   public readonly windowType: WindowType = WindowTypes.Chat;

   public warningIcon: IconDefinition = faExclamationTriangle;
   public chatIcon: IconDefinition = faComment;
   public finishChatIcon: IconDefinition = faCheckSolid;
   public requeueChatIcon: IconDefinition = WellKnownIconsEnum.CHAT_REQUEUE.icon;

   public keyActions: ShortcutDescription[] = [
      {
         shortcutKey: HOTKEYS_DISABLED,
         action: () => {
            return true;
         },
      },
   ];

   constructor(private store: Store, private sanitizer: DomSanitizer, private chatJsApiService: ChatJsApiService) {
      // nothing to do
   }

   ngOnInit(): void {
      this.isLoading$ = this.store
         .select(LongRunningContextState.isLongRunningContextInProgressForCategory)
         .pipe(map((getContext) => getContext(LongRunningContextCategoryTyp.CHAT_WINDOW, this.chatId.messageId)));

      const chatDetails$ = this.store.select(ChatsSelectors.getChat).pipe(map((getChat) => getChat(this.chatId)));

      this.windowTitle$ = chatDetails$.pipe(
         filter((chatDetails) => !_.isNil(chatDetails)),
         map((chatDetails) => {
            if (_.isNil(chatDetails.peer)) return '';
            if (chatDetails.peer.name && chatDetails.peer.email) return `${chatDetails.peer.name} (${chatDetails.peer.email})`;
            if (chatDetails.peer.name) return chatDetails.peer.name;
            if (chatDetails.peer.email) return chatDetails.peer.email;

            return '';
         })
      );

      combineLatest([this.store.select(SamwebWindowState.isOpen), this.store.select(AppSelectors.isVisible)])
         .pipe(
            tap(([isChatWindowOpenFn, isAppVisible]) => {
               const isChatWindowOpen = isChatWindowOpenFn(this.windowType, this.chatId.messageId);

               const wasChatWindowOpenedAgain = isChatWindowOpen && !this.lastIsChatWindowOpen;
               const isAppVisibleAgainAndChatWindowVisible = isAppVisible && !this.lastIsAppVisible && isChatWindowOpen;

               if (wasChatWindowOpenedAgain || isAppVisibleAgainAndChatWindowVisible) {
                  const action = new ResetNumberOfUnreadMessages(this.chatId);
                  this.store.dispatch(action);
               }

               this.lastIsChatWindowOpen = isChatWindowOpen;
               this.lastIsAppVisible = isAppVisible;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      chatDetails$
         .pipe(
            filter((chatDetails) => !_.isNull(chatDetails)),
            tap((chatDetails) => {
               this.chatDetails = chatDetails;
               this.updateChatUrl();
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(ChatsSelectors.getUserToken)
         .pipe(
            filter((userToken) => _.isEmpty(this.userToken) && !_.isEmpty(userToken)),
            tap((userToken) => {
               this.userToken = userToken;
               this.updateChatUrl();
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(ChatsSelectors.wasClosedByPeer)
         .pipe(
            tap((wasClosedByPeerFn) => {
               this.wasClosedByPeer = wasClosedByPeerFn(this.chatId);
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.chatJsApiService.ACKs.pipe(
         filter((commandACK: CommandACK) => commandACK.chatId.equals(this.chatId)),
         tap((commandACK: CommandACK) => this.handleReceivedApiACK(commandACK)),
         takeUntil(this.destroy$)
      ).subscribe();

      this.chatJsApiService.Commands.pipe(
         filter((command: Command | CommandWithPayload<any>) => command.chatId.equals(this.chatId)),
         tap((command: Command | CommandWithPayload<any>) => this.handleReceivedApiCommand(command)),
         takeUntil(this.destroy$)
      ).subscribe();

      this.chatGuestInfo$ = combineLatest([
         chatDetails$,
         this.store.select(ChatsSelectors.getGuestStatus).pipe(map((getStatus) => getStatus(this.chatId))),
      ]).pipe(
         distinctUntilChanged(),
         map(([details, status]) => new ChatGuestInfo(details, status)),
         takeUntil(this.destroy$)
      );
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   //#region UI Event Handlers
   public onIframeLoad(): void {
      this.initChatIframe();
   }

   public onCompleteChatClicked(): void {
      this.closeChat();
   }

   public onCancelClicked(): void {
      if (this.chatDetails.isHistoricChat || this.wasClosedByPeer) {
         this.closeChat();
         return;
      }

      if (_.isNil(this.window.closePopupControl)) {
         return;
      }

      this.closeTooltipDirective.show(this.window.closePopupControl.nativeElement);
   }

   public onRequeueClicked(): void {
      this.startRequeueChat();
   }

   public onCloseDialogCancel(): void {
      this.closeTooltipDirective.hide();
   }

   public onCloseDialogComplete(): void {
      this.closeChat();
   }
   //#endregion

   //#region Chat JS API Handlers
   private handleReceivedApiACK(commandACK: CommandACK): void {
      if (commandACK.commandType.startsWith(InitChatCommand.commandType)) {
         this.chatIframeInitDone = true;
      }
   }

   private handleReceivedApiCommand(command: Command | CommandWithPayload<any>): void {
      switch (command.type) {
         case UpdateLastMessageCommand.commandType:
            this.handleUpdateLastMessageCommand(command as UpdateLastMessageCommand);
            break;
         case ProcessNewMessageCommand.commandType:
            this.handleProcessNewMessageCommand(command as ProcessNewMessageCommand);
            break;
      }
   }

   private handleUpdateLastMessageCommand(command: UpdateLastMessageCommand): void {
      const action = new UpdateLastMessage(this.chatId, new ChatMessage(command.payload.text, this.mapSender(command.payload.sender)));
      this.store.dispatch(action);
   }

   private handleProcessNewMessageCommand(command: ProcessNewMessageCommand): void {
      const updateLastMessageAction = new UpdateLastMessage(this.chatId, new ChatMessage(command.payload.text, this.mapSender(command.payload.sender)));
      this.store.dispatch(updateLastMessageAction);

      const isChatVisible = this.lastIsChatWindowOpen && this.lastIsAppVisible;

      if (!isChatVisible && updateLastMessageAction.lastMessage.sender !== ChatParticipantType.Agent) {
         const incrementNumberOfUnreadMessagesAction = new IncrementNumberOfUnreadMessages(this.chatId);
         this.store.dispatch(incrementNumberOfUnreadMessagesAction);
      }
   }

   private mapSender(sender: ChatMessageSender): ChatParticipantType {
      if (sender === ChatMessageSender.Agent) return ChatParticipantType.Agent;
      if (sender === ChatMessageSender.Peer) return ChatParticipantType.Peer;

      return ChatParticipantType.Bot;
   }
   //#endregion

   private closeChat(): void {
      this.store.dispatch(new CloseChat(this.chatId));
      this.chatJsApiService.closeChat(this.chatId, this.getChatIframeWindow());
   }

   private startRequeueChat(): void {
      this.store.dispatch(new StartReQueueChat(this.chatId));
   }

   private getChatIframeWindow(): Window | null {
      return this.chatIframe?.first?.nativeElement?.contentWindow;
   }

   private initChatIframe(): void {
      if (this.chatIframeInitDone) return;

      const iframeWindow = this.getChatIframeWindow();

      if (_.isNil(iframeWindow)) {
         if (_.isNil(this.chatIframeInitTimer)) {
            this.chatIframeInitTimer = setInterval(() => this.initChatIframe(), 100);
         }
         return;
      }

      this.chatJsApiService.initChat(this.chatId, iframeWindow);

      clearInterval(this.chatIframeInitTimer);
      this.chatIframeInitTimer = null;
   }

   private updateChatUrl(): void {
      const url = this.buildChatUrl(this.chatDetails.id, this.chatDetails.chatUrl, this.userToken, this.chatDetails.isHistoricChat);

      if (url === this.rawChatUrl) return; // nothing to do

      this.rawChatUrl = url;
      this.chatUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
   }

   private buildChatUrl(chatId: ChatId, rocketChatUrl: string, token: string, useChatUid: boolean): string {
      if (!rocketChatUrl) return '';

      const liveChatUrl = `${rocketChatUrl}${rocketChatUrl.endsWith('/') ? '' : '/'}live/%%ROCKET_LIVECHAT_ID%%?layout=embedded`;
      const liveChatUrlWithTagValues = this.replaceTagsInUrl(liveChatUrl, chatId, token, useChatUid);
      const liveChatWrapperUrl = `/chat/index.html?token=%%ROCKET_CHAT_TOKEN%%&rocketChatUrl=${encodeURI(liveChatUrlWithTagValues)}`;
      const liveChatWrapperUrlWithTagValues = this.replaceTagsInUrl(liveChatWrapperUrl, chatId, token, useChatUid);

      return liveChatWrapperUrlWithTagValues;
   }

   private replaceTagsInUrl(url: string, chatId: ChatId, token: string, useChatUid: boolean): string {
      const tags = new CallTagsForSingleCallRemoteDto();
      if (useChatUid) {
         tags.callTags = this.store.selectSnapshot(CallTagsState.getHistoryCallTagsForCall)(chatId.uid);
      } else {
         tags.callTags = this.store.selectSnapshot(CallTagsState.getTagsForChat)(chatId);
      }

      tags.callTags = { ...tags.callTags, ROCKET_CHAT_TOKEN: token };

      const replaceTags = this.store.selectSnapshot(CallTagsSelectors.getReplaceAllTagsInText);
      const selectedSubscriber = this.store.selectSnapshot(SelectedSubscriberState.selectedSubscriber);
      const replaceResult = replaceTags(url, tags, null, selectedSubscriber);

      return replaceResult.text;
   }
}
